import type { Prisma } from "@prisma/client";
import { 
	participantCompanyNameSelect, 
	participantTypes, 
	participantSelector, 
	participantWithSubmissionFilesSelect, 
	designerTypes, 
	decennialCertificateSelect, 
	contractorByIdSelector, 
	participantAbilityControlSelect,
	updatedParticipantSelector
} from "@dune/vars";
import { S3File } from "../s3/s3.types";

export interface UploadCertificateFormData {
	validityStartDate: string;
	validityEndDate: string;
	activities?: string;
	insurerSiren?: string;
	selectedFile?: S3File | null;
}

export type ParticipantType = (typeof participantTypes)[number];
export type DesignerType = (typeof designerTypes)[number];

export type ParticipantPayload = Prisma.ParticipantGetPayload<{
	select: typeof participantSelector;
}>; 

export type ContractorDetailsItem = Prisma.ParticipantGetPayload<{
	select: typeof contractorByIdSelector;
}>;

export type UpdatedParticipantItem = Prisma.ParticipantGetPayload<{
	select: typeof updatedParticipantSelector;
}>;

export type ParticipantWithSubmissionFilesPayload = Prisma.ParticipantGetPayload<{
	select: ReturnType<typeof participantWithSubmissionFilesSelect>;
}>;

export type ParticipantCompanyName = Prisma.ParticipantGetPayload<{
	select: typeof participantCompanyNameSelect;
}>;

export type DecennialCertificatePayload = Prisma.DecennialCertificateGetPayload<{
	select: typeof decennialCertificateSelect;
}>;

export type ParticipantAbilityControl = Prisma.ParticipantGetPayload<{
	select: typeof participantAbilityControlSelect;
}>;

export const ContractorType = {
	DESIGNER: "DESIGNER",
	BUILDER: "BUILDER", 
	MATERIAL: "MATERIAL",
} as const;

export type ContractorTypeValues = typeof ContractorType[keyof typeof ContractorType];

export const ContractorTypeLabels = {
	DESIGNER: "Concepteur",
	BUILDER: "Réalisateur",
	MATERIAL: "Matériaux",
} as const;

export const DesignerTypeLabels = {
	ARCHITECTE: "Architecte",
	BUREAU_ETUDE: "Bureau d'étude",
	CONTROLEUR_TECHNIQUE: "Contrôleur technique",
	GEOTECHNICIEN: "Géotechnicien",
	MAITRE_OEUVRE: "Maître d'œuvre",
} as const;

export type ContractorListItem = Prisma.ParticipantGetPayload<{
	select: typeof participantSelector;
}>;

export type ContractorFilters = {
	search?: string;
	category?: ContractorTypeValues;
	type?: DesignerType;
	assurer?: string;
	activites?: string[];
};

export interface GetContractorsQueryParams {
	page?: number;
	limit?: number;
	search?: string;
	category?: string;
	type?: string;
	insurer?: string;
}

export const CONTRACTOR_DETAIL_TAB_VALUES = {
	INFORMATIONS: "informations",
	ATTESTATIONS: "attestations",
	CHANTIERS: "chantiers",
} as const;

export type ContractorDetailTabValues = typeof CONTRACTOR_DETAIL_TAB_VALUES[keyof typeof CONTRACTOR_DETAIL_TAB_VALUES]; 

export type DecennialCertificateColumns = NonNullable<
	ContractorDetailsItem["decennialCertificates"]
>[0];

export type ConstructionSiteSubmissionColumns = NonNullable<
	ContractorDetailsItem["participantToConstructionSiteSubmission"]
>[0];

export interface CertificateTabProps {
	certificates: NonNullable<ContractorDetailsItem["decennialCertificates"]>;
	contractorId: number;
}

export interface ConstructionTabProps {
	constructionSites: NonNullable<ContractorDetailsItem["participantToConstructionSiteSubmission"]>;
}

export interface CreateCertificateDto {
	contractorId: number;
	name: string;
	key: string;
	bucket: string;
	mimeType: string;
	validityStartDate: string;
	validityEndDate: string;
	activities?: string[];
	insurerSiren?: string;
}

export interface InformationsTabProps {
	contractor: ContractorDetailsItem;
}

export interface SirenApiHeadOfficeAddress {
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
}

export interface SirenApiResponse {
	denomination?: string;
	headOfficeAddress?: SirenApiHeadOfficeAddress;
}

export interface UpdateParticipantPayload {
	type?: ContractorTypeValues;
	siren?: string;
	denomination?: string;
	headquartersAddress?: string;
}

export interface EditContractorFormData {
	type: ContractorTypeValues;
	siren: string;
	denomination?: string;
	headquartersAddress?: string;
}
